import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

import useApiHelper from "../hooks/useApiHelper";
import { IPartBin, IPartBinPartsResponse, IPartBinsResponse } from "../models/PartBin";

// The parts currently sat in one bin. Kept separate from usePartBins so opening a bin doesn't
// fetch a parts list nobody asked for - it only runs once the user presses View Parts.
export function usePartBinParts(partBinId: string | undefined, enabled: boolean) {
  const { get } = useApiHelper();

  return useQuery({
    queryKey: ["partBinParts", partBinId],
    queryFn: () => get<IPartBinPartsResponse>(`/partbin/${partBinId}/parts`),
    enabled: !!partBinId && enabled,
  });
}

export default function usePartBins() {
  const { get, post, put, del } = useApiHelper();
  const queryClient = useQueryClient();

  const partBins = useQuery({
    queryKey: ["partBins"],
    queryFn: () => get<IPartBinsResponse>("/partbin"),
    staleTime: 1000 * 60 * 5, // 5 minutes
  });

  const create = useMutation(
    (partBin: IPartBin) => post("/partbin", partBin),
    {
      onSuccess: () => {
        queryClient.invalidateQueries(["partBins"]);
      },
    }
  );

  const update = useMutation(
    (partBin: IPartBin) => put(`/partbin/${partBin.id}`, partBin),
    {
      onSuccess: () => {
        queryClient.invalidateQueries(["partBins"]);
      },
    }
  );

  const remove = useMutation(
    (partBinId: string) => del(`/partbin/${partBinId}`),
    {
      onSuccess: () => {
        queryClient.invalidateQueries(["partBins"]);
      },
    }
  );

  return {
    partBins,
    create,
    update,
    remove,
  };
}
